import React, { useState, useEffect } from 'react';
import {
  Settings,
  FolderOpen,
  Tag,
  Layers,
  FormInput,
  BarChart3,
  Plus,
  Edit3,
  Trash2,
  X,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Palette,
  Eye,
  EyeOff,
  Building2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const AdminConfigAtendimento = () => {
  const [activeTab, setActiveTab] = useState('categorias');
  const [loading, setLoading] = useState(true);

  // Dados
  const [categorias, setCategorias] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [campos, setCampos] = useState([]);
  const [setores, setSetores] = useState([]);
  const [metricas, setMetricas] = useState(null);

  // Configurações do sistema
  const [configuracoes, setConfiguracoes] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);

  // Config por setor
  const [setorSelecionado, setSetorSelecionado] = useState(null);
  const [camposSetor, setCamposSetor] = useState([]);
  const [loadingCamposSetor, setLoadingCamposSetor] = useState(false);

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'categoria', 'tipo', 'subtipo', 'campo'
  const [editingItem, setEditingItem] = useState(null);
  const [expandedCategoria, setExpandedCategoria] = useState(null);
  const [expandedTipo, setExpandedTipo] = useState(null);

  // Forms
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'configuracoes') {
      loadConfiguracoes();
    }
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [catRes, tipoRes, campoRes, setorRes, metRes] = await Promise.all([
        api.get('/config-atendimentos/categorias'),
        api.get('/config-atendimentos/tipos'),
        api.get('/config-atendimentos/campos'),
        api.get('/setores/'),
        api.get('/config-atendimentos/metricas'),
      ]);
      setCategorias(catRes.data);
      setTipos(tipoRes.data);
      setCampos(campoRes.data);
      setSetores(setorRes.data);
      setMetricas(metRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Erro ao carregar dados';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg).join(', ');
      } else if (detail?.msg) {
        errorMsg = detail.msg;
      }
      toast.error(errorMsg);
    }
    setLoading(false);
  };

  const loadConfiguracoes = async () => {
    try {
      const res = await api.get('/config-atendimentos/configuracoes');
      const configMap = {};
      res.data.forEach(c => { configMap[c.chave] = c.valor || ''; });
      setConfiguracoes(configMap);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  const salvarConfiguracoes = async () => {
    setSavingConfig(true);
    try {
      await api.put('/config-atendimentos/configuracoes', configuracoes);
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error('Erro ao salvar configurações');
    }
    setSavingConfig(false);
  };

  // ============================================================
  // HANDLERS
  // ============================================================

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);

    if (type === 'categoria') {
      setFormData({
        nome: item?.nome || '',
        descricao: item?.descricao || '',
        cor: item?.cor || '#3498db',
        icone: item?.icone || 'FolderOpen',
        ordem: item?.ordem || 0,
        ativo: item?.ativo ?? true,
      });
    } else if (type === 'tipo') {
      setFormData({
        categoria_id: item?.categoria_id || '',
        nome: item?.nome || '',
        descricao: item?.descricao || '',
        requer_subtipo: item?.requer_subtipo || false,
        requer_guia: item?.requer_guia || false,
        requer_valor: item?.requer_valor || false,
        ordem: item?.ordem || 0,
        ativo: item?.ativo ?? true,
      });
    } else if (type === 'subtipo') {
      setFormData({
        tipo_id: item?.tipo_id || '',
        nome: item?.nome || '',
        ordem: item?.ordem || 0,
        ativo: item?.ativo ?? true,
      });
    } else if (type === 'campo') {
      setFormData({
        nome: item?.nome || '',
        label: item?.label || '',
        tipo: item?.tipo || 'texto',
        opcoes: item?.opcoes || [],
        placeholder: item?.placeholder || '',
        obrigatorio: item?.obrigatorio || false,
        ordem: item?.ordem || 0,
        ativo: item?.ativo ?? true,
      });
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setEditingItem(null);
    setFormData({});
  };

  const handleSave = async () => {
    if (!formData.nome && modalType !== 'campo') {
      toast.error('Nome e obrigatorio');
      return;
    }
    if (modalType === 'campo' && !formData.label) {
      toast.error('Label e obrigatorio');
      return;
    }

    setSaving(true);
    try {
      let url = '/config-atendimentos/';
      let method = 'post';

      if (modalType === 'categoria') {
        url += 'categorias';
        if (editingItem?.id) {
          url += `/${editingItem.id}`;
          method = 'put';
        }
      } else if (modalType === 'tipo') {
        url += 'tipos';
        if (editingItem?.id) {
          url += `/${editingItem.id}`;
          method = 'put';
        }
      } else if (modalType === 'subtipo') {
        url += 'subtipos';
      } else if (modalType === 'campo') {
        url += 'campos';
        if (editingItem?.id) {
          url += `/${editingItem.id}`;
          method = 'put';
        }
      }

      if (method === 'post') {
        await api.post(url, formData);
      } else {
        await api.put(url, formData);
      }

      toast.success(editingItem ? 'Atualizado com sucesso' : 'Criado com sucesso');
      closeModal();
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Erro ao salvar';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        // FastAPI retorna array de erros de validacao
        errorMsg = detail.map(e => typeof e === 'string' ? e : e.msg || JSON.stringify(e)).join(', ');
      } else if (typeof detail?.msg === 'string') {
        errorMsg = detail.msg;
      }
      toast.error(String(errorMsg));
    }
    setSaving(false);
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Tem certeza que deseja excluir?')) return;

    try {
      await api.delete(`/config-atendimentos/${type}/${id}`);
      toast.success('Removido com sucesso');
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Erro ao remover';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg).join(', ');
      } else if (detail?.msg) {
        errorMsg = detail.msg;
      }
      toast.error(errorMsg);
    }
  };

  // ============================================================
  // CONFIG POR SETOR
  // ============================================================

  const loadCamposSetor = async (setorId) => {
    setLoadingCamposSetor(true);
    try {
      const response = await api.get(`/config-atendimentos/setor/${setorId}/campos`);
      // Se nao tem config, criar lista baseada nos campos globais
      if (response.data.length === 0 && campos.length > 0) {
        const configsIniciais = campos.map((campo, index) => ({
          campo_id: campo.id,
          campo: campo,
          visivel: true,
          obrigatorio: campo.obrigatorio || false,
          ordem: index,
        }));
        setCamposSetor(configsIniciais);
      } else {
        setCamposSetor(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar campos do setor:', error);
      setCamposSetor([]);
    }
    setLoadingCamposSetor(false);
  };

  const handleSetorChange = (setorId) => {
    const setor = setores.find(s => s.id === parseInt(setorId));
    setSetorSelecionado(setor);
    if (setorId) {
      loadCamposSetor(setorId);
    } else {
      setCamposSetor([]);
    }
  };

  const toggleCampoVisivel = (campoId) => {
    setCamposSetor(prev => prev.map(c =>
      c.campo_id === campoId ? { ...c, visivel: !c.visivel } : c
    ));
  };

  const toggleCampoObrigatorio = (campoId) => {
    setCamposSetor(prev => prev.map(c =>
      c.campo_id === campoId ? { ...c, obrigatorio: !c.obrigatorio } : c
    ));
  };

  const salvarConfigSetor = async () => {
    if (!setorSelecionado) return;

    setSaving(true);
    try {
      const configs = camposSetor.map((c, index) => ({
        campo_id: c.campo_id,
        visivel: c.visivel,
        obrigatorio: c.obrigatorio,
        ordem: index,
      }));

      await api.post(`/config-atendimentos/setor/${setorSelecionado.id}/campos`, configs);
      toast.success('Configuracao salva com sucesso');
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Erro ao salvar configuracao';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg).join(', ');
      } else if (detail?.msg) {
        errorMsg = detail.msg;
      }
      toast.error(errorMsg);
    }
    setSaving(false);
  };

  // ============================================================
  // RENDER
  // ============================================================

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ color: 'var(--primary)', marginBottom: '0.5rem' }}>
          <Settings size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Configuracao de Atendimentos
        </h2>
        <p style={{ color: 'var(--gray-500)', fontSize: '0.9rem' }}>
          Configure categorias, tipos e campos para classificar atendimentos
        </p>
      </div>

      {/* Metricas Rapidas */}
      {metricas && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          <div className="search-box" style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>
              {categorias.length}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>Categorias</div>
          </div>
          <div className="search-box" style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#27ae60' }}>
              {tipos.length}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>Tipos</div>
          </div>
          <div className="search-box" style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f39c12' }}>
              {campos.length}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>Campos</div>
          </div>
          <div className="search-box" style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#e74c3c' }}>
              {metricas.total}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>Atendimentos (30d)</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid var(--gray-200)', paddingBottom: '0.5rem' }}>
        {[
          { id: 'categorias', label: 'Categorias', icon: FolderOpen },
          { id: 'tipos', label: 'Tipos', icon: Tag },
          { id: 'campos', label: 'Campos', icon: FormInput },
          { id: 'setores', label: 'Por Setor', icon: Building2 },
          { id: 'metricas', label: 'Metricas', icon: BarChart3 },
          { id: 'configuracoes', label: 'Configurações', icon: Settings },
        ].map((tab) => (
          <button
            key={tab.id}
            className={`btn ${activeTab === tab.id ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="search-box" style={{ padding: '1.5rem' }}>
        {/* CATEGORIAS */}
        {activeTab === 'categorias' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>Categorias de Atendimento</h3>
              <button className="btn btn-primary" onClick={() => openModal('categoria')}>
                <Plus size={16} />
                Nova Categoria
              </button>
            </div>

            {categorias.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--gray-500)', padding: '2rem' }}>
                Nenhuma categoria cadastrada
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {categorias.map((cat) => {
                  const tiposCat = tipos.filter((t) => t.categoria_id === cat.id);
                  const isExpanded = expandedCategoria === cat.id;

                  return (
                    <div key={cat.id}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '1rem',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          borderLeft: `4px solid ${cat.cor}`,
                        }}
                      >
                        <button
                          style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '0.5rem' }}
                          onClick={() => setExpandedCategoria(isExpanded ? null : cat.id)}
                        >
                          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        </button>
                        <div
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '6px',
                            background: cat.cor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '1rem',
                          }}
                        >
                          <FolderOpen size={18} color="white" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>{cat.nome}</div>
                          <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                            {tiposCat.length} tipo(s)
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button className="btn btn-secondary" style={{ padding: '0.4rem 0.6rem' }} onClick={() => openModal('tipo', { categoria_id: cat.id })}>
                            <Plus size={14} />
                          </button>
                          <button className="btn btn-secondary" style={{ padding: '0.4rem 0.6rem' }} onClick={() => openModal('categoria', cat)}>
                            <Edit3 size={14} />
                          </button>
                          <button className="btn btn-secondary" style={{ padding: '0.4rem 0.6rem', color: 'var(--danger)' }} onClick={() => handleDelete('categorias', cat.id)}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Tipos da categoria */}
                      {isExpanded && tiposCat.length > 0 && (
                        <div style={{ marginLeft: '2rem', marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          {tiposCat.map((tipo) => {
                            const isTipoExpanded = expandedTipo === tipo.id;
                            return (
                              <div key={tipo.id}>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '0.75rem',
                                    background: 'white',
                                    borderRadius: '6px',
                                    border: '1px solid var(--gray-200)',
                                  }}
                                >
                                  <button
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '0.5rem' }}
                                    onClick={() => setExpandedTipo(isTipoExpanded ? null : tipo.id)}
                                    disabled={!tipo.requer_subtipo}
                                  >
                                    {tipo.requer_subtipo ? (isTipoExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />) : <span style={{ width: '16px' }} />}
                                  </button>
                                  <Tag size={16} style={{ marginRight: '0.5rem', color: cat.cor }} />
                                  <span style={{ flex: 1 }}>{tipo.nome}</span>
                                  {tipo.requer_subtipo && (
                                    <span style={{ fontSize: '0.75rem', background: '#e8f4f8', padding: '0.2rem 0.5rem', borderRadius: '4px', marginRight: '0.5rem' }}>
                                      subtipos
                                    </span>
                                  )}
                                  <button className="btn btn-secondary" style={{ padding: '0.3rem 0.5rem', fontSize: '0.8rem' }} onClick={() => openModal('tipo', tipo)}>
                                    <Edit3 size={12} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TIPOS */}
        {activeTab === 'tipos' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>Tipos de Atendimento</h3>
              <button className="btn btn-primary" onClick={() => openModal('tipo')}>
                <Plus size={16} />
                Novo Tipo
              </button>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <select
                className="form-control"
                style={{ maxWidth: '300px' }}
                onChange={(e) => {
                  // Filtrar por categoria
                }}
              >
                <option value="">Todas as categorias</option>
                {categorias.map((c) => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </select>
            </div>

            <table className="data-table">
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Nome</th>
                  <th>Subtipo?</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tipos.map((tipo) => {
                  const cat = categorias.find((c) => c.id === tipo.categoria_id);
                  return (
                    <tr key={tipo.id}>
                      <td>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: cat?.cor || '#ccc' }}></span>
                          {cat?.nome || '-'}
                        </span>
                      </td>
                      <td>{tipo.nome}</td>
                      <td>{tipo.requer_subtipo ? 'Sim' : 'Nao'}</td>
                      <td>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          background: tipo.ativo ? '#e8f5e9' : '#ffebee',
                          color: tipo.ativo ? '#27ae60' : '#e74c3c',
                        }}>
                          {tipo.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-secondary" style={{ padding: '0.3rem 0.5rem' }} onClick={() => openModal('tipo', tipo)}>
                          <Edit3 size={14} />
                        </button>
                        <button className="btn btn-secondary" style={{ padding: '0.3rem 0.5rem', color: 'var(--danger)', marginLeft: '0.25rem' }} onClick={() => handleDelete('tipos', tipo.id)}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* CAMPOS */}
        {activeTab === 'campos' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>Campos Customizados</h3>
              <button className="btn btn-primary" onClick={() => openModal('campo')}>
                <Plus size={16} />
                Novo Campo
              </button>
            </div>

            <table className="data-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Nome (slug)</th>
                  <th>Tipo</th>
                  <th>Obrig.</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {campos.map((campo) => (
                  <tr key={campo.id}>
                    <td>{campo.label}</td>
                    <td><code style={{ background: 'var(--gray-100)', padding: '0.2rem 0.4rem', borderRadius: '4px' }}>{campo.nome}</code></td>
                    <td>{campo.tipo}</td>
                    <td>{campo.obrigatorio ? 'Sim' : 'Nao'}</td>
                    <td>
                      <button className="btn btn-secondary" style={{ padding: '0.3rem 0.5rem' }} onClick={() => openModal('campo', campo)}>
                        <Edit3 size={14} />
                      </button>
                      <button className="btn btn-secondary" style={{ padding: '0.3rem 0.5rem', color: 'var(--danger)', marginLeft: '0.25rem' }} onClick={() => handleDelete('campos', campo.id)}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* CONFIG POR SETOR */}
        {activeTab === 'setores' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>Configuracao de Campos por Setor</h3>
            </div>

            <p style={{ color: 'var(--gray-500)', fontSize: '0.9rem', marginBottom: '1rem' }}>
              Selecione um setor para configurar quais campos serao exibidos e quais sao obrigatorios.
              As categorias e tipos sao comuns a todos os setores.
            </p>

            <div className="form-group" style={{ maxWidth: '400px' }}>
              <label>Setor</label>
              <select
                className="form-control"
                value={setorSelecionado?.id || ''}
                onChange={(e) => handleSetorChange(e.target.value)}
              >
                <option value="">Selecione um setor...</option>
                {setores.map((s) => (
                  <option key={s.id} value={s.id}>{s.nome}</option>
                ))}
              </select>
            </div>

            {setorSelecionado && (
              <>
                {loadingCamposSetor ? (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <div className="spinner"></div>
                  </div>
                ) : campos.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--gray-500)', padding: '2rem', marginTop: '1rem' }}>
                    Nenhum campo customizado cadastrado. Va para a aba "Campos" para criar.
                  </div>
                ) : (
                  <div style={{ marginTop: '1.5rem' }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 100px 100px 50px',
                      gap: '0.5rem',
                      padding: '0.75rem',
                      background: 'var(--gray-100)',
                      borderRadius: '8px',
                      marginBottom: '0.5rem',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                    }}>
                      <div>Campo</div>
                      <div style={{ textAlign: 'center' }}>Visivel</div>
                      <div style={{ textAlign: 'center' }}>Obrigatorio</div>
                      <div></div>
                    </div>

                    {camposSetor.map((config, index) => (
                      <div
                        key={config.campo_id || index}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 100px 100px 50px',
                          gap: '0.5rem',
                          padding: '0.75rem',
                          background: config.visivel ? 'white' : 'var(--gray-50)',
                          borderRadius: '8px',
                          marginBottom: '0.5rem',
                          border: '1px solid var(--gray-200)',
                          opacity: config.visivel ? 1 : 0.6,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 500 }}>{config.campo?.label || config.campo_id}</div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>
                            {config.campo?.tipo || '-'} | <code style={{ fontSize: '0.75rem' }}>{config.campo?.nome}</code>
                          </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <button
                            className={`btn ${config.visivel ? 'btn-success' : 'btn-secondary'}`}
                            style={{ padding: '0.3rem 0.5rem' }}
                            onClick={() => toggleCampoVisivel(config.campo_id)}
                          >
                            {config.visivel ? <Eye size={16} /> : <EyeOff size={16} />}
                          </button>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={config.obrigatorio}
                            onChange={() => toggleCampoObrigatorio(config.campo_id)}
                            disabled={!config.visivel}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                        </div>
                        <div style={{ textAlign: 'center', color: 'var(--gray-400)', cursor: 'grab' }}>
                          <GripVertical size={16} />
                        </div>
                      </div>
                    ))}

                    <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
                      <button
                        className="btn btn-primary"
                        onClick={salvarConfigSetor}
                        disabled={saving}
                      >
                        {saving ? 'Salvando...' : 'Salvar Configuracao'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* METRICAS */}
        {activeTab === 'metricas' && metricas && (
          <div>
            <h3 style={{ marginBottom: '1.5rem' }}>Metricas de Atendimentos (30 dias)</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              {/* Por Categoria */}
              <div>
                <h4 style={{ marginBottom: '1rem', color: 'var(--gray-600)' }}>Por Categoria</h4>
                {metricas.por_categoria.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: item.cor || '#3498db' }}></span>
                    <span style={{ flex: 1 }}>{item.nome || 'Sem categoria'}</span>
                    <span style={{ fontWeight: 600 }}>{item.total}</span>
                  </div>
                ))}
              </div>

              {/* Por Tipo */}
              <div>
                <h4 style={{ marginBottom: '1rem', color: 'var(--gray-600)' }}>Por Tipo</h4>
                {metricas.por_tipo.slice(0, 10).map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <Tag size={14} style={{ color: 'var(--gray-400)' }} />
                    <span style={{ flex: 1 }}>{item.nome || 'Sem tipo'}</span>
                    <span style={{ fontWeight: 600 }}>{item.total}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CONFIGURAÇÕES */}
        {activeTab === 'configuracoes' && (
          <div>
            <h3 style={{ marginBottom: '1.5rem', color: 'var(--gray-700)' }}>
              <Settings size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Configurações de E-mail de Acompanhamento
            </h3>

            {/* Toggle Ativo/Inativo */}
            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Envio de e-mail de acompanhamento</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                    Quando ativo, envia e-mails automáticos de acompanhamento ao beneficiário
                  </div>
                </div>
                <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={configuracoes.email_acompanhamento_ativo === 'true'}
                    onChange={(e) => setConfiguracoes({ ...configuracoes, email_acompanhamento_ativo: e.target.checked ? 'true' : 'false' })}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: configuracoes.email_acompanhamento_ativo === 'true' ? 'var(--primary)' : 'var(--gray-300)',
                    borderRadius: '26px', transition: '0.3s',
                  }}>
                    <span style={{
                      position: 'absolute', height: '20px', width: '20px', left: configuracoes.email_acompanhamento_ativo === 'true' ? '26px' : '3px',
                      bottom: '3px', backgroundColor: 'white', borderRadius: '50%', transition: '0.3s',
                    }} />
                  </span>
                </label>
              </div>
            </div>

            {/* Modo */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div className="form-group">
                <label style={{ fontWeight: 600 }}>Modo de envio</label>
                <select
                  className="form-control"
                  value={configuracoes.email_acompanhamento_modo || 'teste'}
                  onChange={(e) => setConfiguracoes({ ...configuracoes, email_acompanhamento_modo: e.target.value })}
                  style={{ maxWidth: '300px' }}
                >
                  <option value="teste">Teste (envia apenas para e-mail de teste)</option>
                  <option value="producao">Produção (envia para beneficiários)</option>
                </select>
                <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                  Em modo teste, todos os e-mails serão enviados para o endereço de teste abaixo
                </div>
              </div>
            </div>

            {/* E-mail de teste */}
            <div style={{ marginBottom: '2rem' }}>
              <div className="form-group">
                <label style={{ fontWeight: 600 }}>E-mail para teste</label>
                <input
                  className="form-control"
                  type="email"
                  value={configuracoes.email_acompanhamento_teste || ''}
                  onChange={(e) => setConfiguracoes({ ...configuracoes, email_acompanhamento_teste: e.target.value })}
                  placeholder="teste@empresa.com.br"
                  style={{ maxWidth: '400px' }}
                />
                <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                  Quando em modo teste, os e-mails serão enviados para este endereço
                </div>
              </div>
            </div>

            {/* Mostrar todos atendimentos */}
            <div style={{ marginBottom: '2rem' }}>
              <div className="form-group">
                <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                  Mostrar todos atendimentos na página de acompanhamento
                </label>
                <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={configuracoes.email_acompanhamento_mostrar_todos === 'true'}
                    onChange={(e) => setConfiguracoes({ ...configuracoes, email_acompanhamento_mostrar_todos: e.target.checked ? 'true' : 'false' })}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: configuracoes.email_acompanhamento_mostrar_todos === 'true' ? 'var(--primary)' : 'var(--gray-300)',
                    borderRadius: '26px', transition: '0.3s',
                  }}>
                    <span style={{
                      position: 'absolute', height: '20px', width: '20px', left: configuracoes.email_acompanhamento_mostrar_todos === 'true' ? '26px' : '3px',
                      bottom: '3px', backgroundColor: 'white', borderRadius: '50%', transition: '0.3s',
                    }} />
                  </span>
                </label>
                <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: '0.5rem' }}>
                  Quando ativo, a página de acompanhamento mostra todos os atendimentos do beneficiário.
                  Quando desativado, mostra apenas o protocolo específico do link.
                </div>
              </div>
            </div>

            {/* Botão salvar */}
            <button
              className="btn btn-primary"
              onClick={salvarConfiguracoes}
              disabled={savingConfig}
              style={{ minWidth: '150px' }}
            >
              {savingConfig ? 'Salvando...' : 'Salvar Configurações'}
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>
                {editingItem ? 'Editar' : 'Novo'} {modalType === 'categoria' ? 'Categoria' : modalType === 'tipo' ? 'Tipo' : modalType === 'subtipo' ? 'Subtipo' : 'Campo'}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              {/* Categoria Form */}
              {modalType === 'categoria' && (
                <>
                  <div className="form-group">
                    <label>Nome *</label>
                    <input
                      className="form-control"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      placeholder="Ex: Reembolso, Autorizacao..."
                    />
                  </div>
                  <div className="form-group">
                    <label>Descricao</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Cor</label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input
                          type="color"
                          value={formData.cor}
                          onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                          style={{ width: '40px', height: '36px', border: 'none', cursor: 'pointer' }}
                        />
                        <input
                          className="form-control"
                          value={formData.cor}
                          onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Ordem</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.ordem}
                        onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Tipo Form */}
              {modalType === 'tipo' && (
                <>
                  <div className="form-group">
                    <label>Categoria *</label>
                    <select
                      className="form-control"
                      value={formData.categoria_id}
                      onChange={(e) => setFormData({ ...formData, categoria_id: e.target.value })}
                    >
                      <option value="">Selecione...</option>
                      {categorias.map((c) => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Nome *</label>
                    <input
                      className="form-control"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      placeholder="Ex: Consulta, Exame, Procedimento..."
                    />
                  </div>
                  <div className="form-group">
                    <label>Descricao</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.requer_subtipo}
                        onChange={(e) => setFormData({ ...formData, requer_subtipo: e.target.checked })}
                      />
                      Requer Subtipo
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.requer_guia}
                        onChange={(e) => setFormData({ ...formData, requer_guia: e.target.checked })}
                      />
                      Requer Guia
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.requer_valor}
                        onChange={(e) => setFormData({ ...formData, requer_valor: e.target.checked })}
                      />
                      Requer Valor
                    </label>
                  </div>
                </>
              )}

              {/* Campo Form */}
              {modalType === 'campo' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Label (exibido) *</label>
                      <input
                        className="form-control"
                        value={formData.label}
                        onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                        placeholder="Ex: Valor do Reembolso"
                      />
                    </div>
                    <div className="form-group">
                      <label>Nome (slug) *</label>
                      <input
                        className="form-control"
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                        placeholder="Ex: valor_reembolso"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Tipo</label>
                    <select
                      className="form-control"
                      value={formData.tipo}
                      onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                    >
                      <option value="texto">Texto</option>
                      <option value="textarea">Texto Longo</option>
                      <option value="numero">Numero</option>
                      <option value="moeda">Moeda</option>
                      <option value="data">Data</option>
                      <option value="select">Selecao</option>
                    </select>
                  </div>
                  {formData.tipo === 'select' && (
                    <div className="form-group">
                      <label>Opcoes (uma por linha)</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={formData.opcoes?.join('\n') || ''}
                        onChange={(e) => setFormData({ ...formData, opcoes: e.target.value.split('\n').filter(Boolean) })}
                        placeholder="Opcao 1&#10;Opcao 2&#10;Opcao 3"
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Placeholder</label>
                    <input
                      className="form-control"
                      value={formData.placeholder}
                      onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
                    />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.obrigatorio}
                      onChange={(e) => setFormData({ ...formData, obrigatorio: e.target.checked })}
                    />
                    Obrigatorio
                  </label>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminConfigAtendimento;
